import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";

/* hs-eslint ignored failing-rules */

/* eslint-disable @typescript-eslint/no-floating-promises */
import FormattedMessage from 'I18n/components/FormattedMessage';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import Raven from 'raven-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { getIsRetailPortal } from 'ui-addon-upgrades/_core/common/api/fetchRetail';
import { sourceKeys } from 'ui-addon-upgrades/_core/common/data/upgradeData/properties/sources';
import { getPropertyRepInfo } from 'ui-addon-upgrades/_core/common/data/upgradeData/transformers/getPropertyRepInfo';
import { ContactSalesButtonPropsInterface } from 'ui-addon-upgrades/_core/contactSales/ContactSalesPropsInterface';
import { correctUpgradeData, overrideAssignable, sequence, trackCommModalClose, trackContactSalesButtonClick, trackContactSalesButtonView } from 'ui-addon-upgrades/_core/contactSales/utils';
import { trackPotentialBannerInteraction } from 'ui-addon-upgrades/ums/utils';
import { debounce, isUndefined, omit } from 'ui-addon-upgrades/utils';
import UIPricingPageRedirectButton from '../../button/UIPricingPageRedirectButton';
import { mapPurchaseMotionApiName } from '../../ums/utils';
import { prefetchMeetingData } from '../common/api/meetingHelpers';
import { TALK_TO_SALES_BUTTON } from '../common/constants/PurchaseMotionLocations';
import { getParsedProductPurchaseMotions } from '../common/data/purchaseMotionData/getParsedProductPurchaseMotions';
import { getCanViewNewSeats } from '../common/eligibility/getCanViewNewSeats';
import { getIsFreePortal } from '../common/eligibility/getIsFreePortal';
import { PRICING_PAGE_REDIRECT, TALK_TO_SALES, TALK_TO_SALES_FALLBACK } from '../purchaseMotions/PurchaseMotionTypes';
import CommModalContainer from './CommModalContainer';
const MODAL_STATE = {
  CLOSED: 'CLOSED',
  LOADING: 'LOADING',
  OPEN: 'OPEN'
};

const correctUpgradeDataHOC = Component => props => {
  return /*#__PURE__*/_jsx(Component, Object.assign({}, props, {
    upgradeData: correctUpgradeData(props.upgradeData, props.canViewNewSeats),
    isAssignable: overrideAssignable(props.isAssignable)
  }));
};

const ContactSalesButton = props => {
  const {
    isAssignable,
    onClick = () => {},
    onCancel = () => {}
  } = props;
  const [ttsPurchaseMotion, setTtsPurchaseMotion] = useState(undefined);
  const [repInfoPromise, setRepInfoPromise] = useState(undefined);
  const [isFreePortalPromise, setIsFreePortalPromise] = useState(undefined);
  const [isRetailPortalPromise, setIsRetailPortalPromise] = useState(undefined);
  const [modalState, setModalState] = useState(MODAL_STATE.CLOSED);
  const [canViewNewSeats, setCanViewNewSeats] = useState(undefined);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const isFreePortal = useRef();
  const isRetailPortal = useRef();
  const repInfo = useRef();
  const isMounted = useRef(true);
  useEffect(() => {
    if (typeof props.canViewNewSeats === 'undefined' && typeof canViewNewSeats === 'undefined') {
      getCanViewNewSeats().then(res => {
        setCanViewNewSeats(res);
        props.upgradeData.apiName = mapPurchaseMotionApiName(res, props.upgradeData.upgradeProduct, 'ContactSalesButton', props.upgradeData.apiName);
      }).catch(() => {
        setCanViewNewSeats(false);
      });
    }
  }, [props.upgradeData, props.canViewNewSeats, canViewNewSeats]);
  useEffect(() => {
    if ((typeof props.canViewNewSeats !== 'undefined' || typeof canViewNewSeats !== 'undefined') && !hasTrackedView) {
      trackContactSalesButtonView(props);
      setHasTrackedView(true);
    }

    setRepInfoPromise(getPropertyRepInfo(props.upgradeData, sourceKeys.CONTACT_SALES_BUTTON, props));
  }, [props, canViewNewSeats, hasTrackedView]);
  useEffect(() => {
    if (isFreePortalPromise === undefined) {
      setIsFreePortalPromise(getIsFreePortal());
    }

    if (isRetailPortalPromise === undefined) {
      setIsRetailPortalPromise(getIsRetailPortal());
    }

    prefetchMeetingData();
  }, [isFreePortalPromise, isRetailPortalPromise]);
  useEffect(() => {
    // Fetch {purchaseMotions} to compute {isAssignable} ONLY IF:
    // {isAssignable} prop was not passed (we need to compute the value) AND
    // {ttsPurchaseMotion} is undefined (we have not fetched before) AND
    // {apiName} is defined
    if (isUndefined(isAssignable) && isUndefined(ttsPurchaseMotion) && props.upgradeData.apiName) {
      getParsedProductPurchaseMotions({
        location: TALK_TO_SALES_BUTTON,
        apiName: props.upgradeData.apiName
      }).then(purchaseMotions => {
        // TALK_TO_SALES_BUTTON location only returns one purchase motion
        const purchaseMotion = purchaseMotions && purchaseMotions[0] || TALK_TO_SALES_FALLBACK;

        if (isMounted.current) {
          setTtsPurchaseMotion(purchaseMotion);
        }
      }).catch(() => {
        if (isMounted.current) {
          setTtsPurchaseMotion(TALK_TO_SALES_FALLBACK);
        }
      });
    }

    return function cleanup() {
      isMounted.current = false;
    }; // should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, props.upgradeData.apiName]);
  const computedIsAssignable = useCallback(() => {
    return isAssignable || ttsPurchaseMotion === TALK_TO_SALES;
  }, [isAssignable, ttsPurchaseMotion]);
  const handleClick = useCallback(() => {
    setModalState(MODAL_STATE.LOADING);
    const {
      pointType,
      upgradeKey
    } = props.upgradeData;
    trackPotentialBannerInteraction(pointType, upgradeKey).catch(error => {
      Raven.captureException(new Error('Failed to submit interaction from UIContactSalesButton'), {
        extra: {
          error
        }
      });
    });
    Promise.all([repInfoPromise, isFreePortalPromise, isRetailPortalPromise]).then(([repInfoResponse, isFreePortalResponse, isRetailPortalResponse]) => {
      repInfo.current = repInfoResponse && repInfoResponse.repInfo ? repInfoResponse.repInfo : {};
      isFreePortal.current = isFreePortalResponse;
      isRetailPortal.current = isRetailPortalResponse;
    }).catch(() => {
      repInfo.current = {};
      isFreePortal.current = true;
      isRetailPortal.current = false;
      Raven.captureMessage('Communication methods modal opened without setting data');
    }).finally(() => {
      trackContactSalesButtonClick(props, Boolean(isRetailPortal.current), repInfo.current);
      setModalState(MODAL_STATE.OPEN);
    });
  }, [isFreePortalPromise, isRetailPortalPromise, props, repInfoPromise]);

  const handleClose = () => {
    trackCommModalClose(props.upgradeData, repInfo.current);
    setModalState(MODAL_STATE.CLOSED);
    onCancel();
  };

  const renderButton = () => {
    const CLICK_DELAY = 50;
    const debouncedHandleClick = debounce(handleClick, CLICK_DELAY);
    const onClickSequence = sequence(debouncedHandleClick, onClick);
    const propsToPass = omit(props, ['usageLimitBannerData', 'forwardedRef', 'onClick', 'upgradeData', 'allowModal', 'isAssignable', 'termId', 'additionalUnitQuantity', 'isUpgradePoint', '_textOverride', '_overrideCommunicationMethodKey', '_buttonOverride', '_overrideModalHeaderText', 'modalRoot', 'canViewNewSeats']);

    if (ttsPurchaseMotion === PRICING_PAGE_REDIRECT) {
      // A pp redirect motion will only be returned for users in Russia
      return /*#__PURE__*/_jsx(UIPricingPageRedirectButton, Object.assign({
        upgradeData: props.upgradeData
      }, propsToPass));
    } else if (props._buttonOverride) {
      return /*#__PURE__*/_jsx("div", Object.assign({
        onClick: onClickSequence,
        "data-selenium": "contact-sales-button",
        "data-test-id": "contact-sales-button-override",
        "data-reagan-primary-cta": true
      }, propsToPass, {
        children: props._buttonOverride
      }));
    } else {
      return /*#__PURE__*/_jsx(UILoadingButton, Object.assign({
        loading: modalState === MODAL_STATE.LOADING,
        buttonRef: props.forwardedRef,
        onClick: onClickSequence,
        "data-selenium": "contact-sales-button",
        "data-test-id": "contact-sales-button",
        "data-reagan-primary-cta": true
      }, propsToPass, {
        children: props._textOverride || /*#__PURE__*/_jsx(FormattedMessage, {
          message: "upgrades.contactSalesButton.text"
        })
      }));
    }
  };

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderButton(), modalState === MODAL_STATE.OPEN && /*#__PURE__*/_jsx(CommModalContainer, {
      handleClose: handleClose,
      isFreePortal: Boolean(isFreePortal.current),
      isRetailPortal: Boolean(isRetailPortal.current),
      isAssignable: computedIsAssignable(),
      modalRoot: props.modalRoot,
      upgradeData: props.upgradeData,
      repInfo: repInfo.current,
      _overrideCommunicationMethodKey: props._overrideCommunicationMethodKey,
      _overrideModalHeaderText: props._overrideModalHeaderText,
      isUpgradePoint: props.isUpgradePoint
    })]
  });
};

ContactSalesButton.defaultProps = {
  use: 'primary'
};
ContactSalesButton.propTypes = ContactSalesButtonPropsInterface;
export default correctUpgradeDataHOC(ContactSalesButton);